import { apiClient } from './api';
import type { OrgMembership, TeamMember, TeamInvitation, TeamRole, ApiResponse } from '@/types';

class TeamService {
  // Org switching
  async getOrganizations(): Promise<OrgMembership[]> {
    const response = await apiClient.get<ApiResponse<OrgMembership[]>>('/auth/organizations');
    return response.data.data || [];
  }

  async switchOrganization(organizationId: string): Promise<{ access_token: string; refresh_token: string; expires_in: number }> {
    const response = await apiClient.post<ApiResponse<{ user: any; tokens: { access_token: string; refresh_token: string; expires_in: number } }>>(
      '/auth/switch-org',
      { organization_id: organizationId }
    );
    const tokens = response.data.data?.tokens;
    if (!tokens) throw new Error('Failed to switch organization');
    localStorage.setItem('access_token', tokens.access_token);
    localStorage.setItem('refresh_token', tokens.refresh_token);
    return tokens;
  }

  // Members
  async getMembers(): Promise<TeamMember[]> {
    const response = await apiClient.get<ApiResponse<TeamMember[]>>('/team/members');
    return response.data.data || [];
  }

  async updateMemberRole(userId: string, role: string): Promise<void> {
    await apiClient.patch(`/team/members/${userId}`, { role });
  }

  async removeMember(userId: string): Promise<void> {
    await apiClient.delete(`/team/members/${userId}`);
  }

  // Invitations
  async inviteMember(email: string, role: string): Promise<TeamInvitation> {
    const response = await apiClient.post<ApiResponse<TeamInvitation>>('/team/invite', { email, role });
    if (response.data.data) return response.data.data;
    throw new Error('Failed to invite member');
  }

  async getInvitations(): Promise<TeamInvitation[]> {
    const response = await apiClient.get<ApiResponse<TeamInvitation[]>>('/team/invitations');
    return response.data.data || [];
  }

  async revokeInvitation(id: string): Promise<void> {
    await apiClient.delete(`/team/invitations/${id}`);
  }

  async getMyInvitations(): Promise<TeamInvitation[]> {
    const response = await apiClient.get<ApiResponse<TeamInvitation[]>>('/team/my-invitations');
    return response.data.data || [];
  }

  async acceptInvitation(token: string): Promise<void> {
    await apiClient.post(`/team/invitations/${token}/accept`);
  }

  async declineInvitation(token: string): Promise<void> {
    await apiClient.post(`/team/invitations/${token}/decline`);
  }

  // Roles
  async getRoles(): Promise<TeamRole[]> {
    const response = await apiClient.get<ApiResponse<TeamRole[]>>('/team/roles');
    return response.data.data || [];
  }

  async createRole(data: { name: string; description?: string; permissions: Record<string, boolean> }): Promise<TeamRole> {
    const response = await apiClient.post<ApiResponse<TeamRole>>('/team/roles', data);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to create role');
  }

  async updateRole(id: string, data: { name?: string; description?: string; permissions?: Record<string, boolean> }): Promise<TeamRole> {
    const response = await apiClient.patch<ApiResponse<TeamRole>>(`/team/roles/${id}`, data);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to update role');
  }

  async deleteRole(id: string): Promise<void> {
    await apiClient.delete(`/team/roles/${id}`);
  }
}

export const teamService = new TeamService();
